import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Users,
  Heart,
  TrendingUp,
  Loader2,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import {
  SocialAccount,
  SocialPlatform,
  SocialAnalyticsSnapshot,
  PLATFORM_INFO
} from '@/types/social';
import { cn } from '@/lib/core/utils';

interface PlatformComparisonProps {
  businessId: string;
  accounts: SocialAccount[];
  dateRange: { from: Date; to: Date };
}

interface PlatformStats {
  platform: SocialPlatform;
  accountName: string;
  followers: number;
  engagement: number;
  engagementRate: number;
  reach: number;
  impressions: number;
}

export const PlatformComparison: React.FC<PlatformComparisonProps> = ({
  businessId,
  accounts,
  dateRange
}) => {
  // Fetch latest analytics for each account
  const { data: platformStats, isLoading } = useQuery({
    queryKey: ['platform-comparison', businessId, dateRange],
    queryFn: async () => {
      const stats: PlatformStats[] = [];

      for (const account of accounts) {
        // Get latest snapshot for this account
        const { data: snapshots } = await supabase
          .from('social_analytics_snapshots')
          .select('*')
          .eq('social_account_id', account.id)
          .gte('snapshot_date', format(dateRange.from, 'yyyy-MM-dd'))
          .lte('snapshot_date', format(dateRange.to, 'yyyy-MM-dd'))
          .order('snapshot_date', { ascending: false });

        if (snapshots && snapshots.length > 0) {
          const latestSnapshot = snapshots[0] as SocialAnalyticsSnapshot;
          const totalEngagement = snapshots.reduce((sum, s) =>
            sum + (s.likes_count || 0) + (s.comments_count || 0) +
            (s.shares_count || 0) + (s.saves_count || 0), 0);
          const totalReach = snapshots.reduce((sum, s) => sum + (s.reach || 0), 0);
          const totalImpressions = snapshots.reduce((sum, s) => sum + (s.impressions || 0), 0);

          stats.push({
            platform: account.platform as SocialPlatform,
            accountName: account.account_name || account.account_username || PLATFORM_INFO[account.platform as SocialPlatform].name,
            followers: latestSnapshot.followers_count || 0,
            engagement: totalEngagement,
            engagementRate: latestSnapshot.engagement_rate || 0,
            reach: totalReach,
            impressions: totalImpressions,
          });
        } else {
          // No analytics data, use placeholder
          stats.push({
            platform: account.platform as SocialPlatform,
            accountName: account.account_name || account.account_username || PLATFORM_INFO[account.platform as SocialPlatform].name,
            followers: 0,
            engagement: 0,
            engagementRate: 0,
            reach: 0,
            impressions: 0,
          });
        }
      }

      return stats;
    },
    enabled: accounts.length > 0,
  });

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Calculate max values for progress bars
  const maxFollowers = Math.max(...(platformStats?.map(s => s.followers) || [0]), 1);
  const maxEngagement = Math.max(...(platformStats?.map(s => s.engagement) || [0]), 1);
  const maxReach = Math.max(...(platformStats?.map(s => s.reach) || [0]), 1);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg">Platform Comparison</CardTitle>
            <CardDescription>Compare performance across platforms</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !platformStats || platformStats.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center">
            <p className="text-muted-foreground">No data available</p>
          </div>
        ) : (
          <div className="space-y-6">
            {platformStats.map((stats) => {
              const Icon = getPlatformIcon(stats.platform);
              const platformInfo = PLATFORM_INFO[stats.platform];

              return (
                <div key={stats.platform} className="space-y-3">
                  {/* Platform Header */}
                  <div className="flex items-center gap-3">
                    <div
                      className="rounded-full p-2"
                      style={{ backgroundColor: platformInfo.bgColor }}
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: platformInfo.color }}
                      />
                    </div>
                    <div>
                      <p className="font-medium">{stats.accountName}</p>
                      <p className="text-xs text-muted-foreground">{platformInfo.name}</p>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Followers */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          Followers
                        </span>
                        <span className="font-medium">{formatNumber(stats.followers)}</span>
                      </div>
                      <Progress
                        value={(stats.followers / maxFollowers) * 100}
                        className="h-2"
                        style={{
                          ['--progress-background' as any]: platformInfo.color
                        }}
                      />
                    </div>

                    {/* Engagement */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Heart className="h-3 w-3" />
                          Engagement
                        </span>
                        <span className="font-medium">{formatNumber(stats.engagement)}</span>
                      </div>
                      <Progress
                        value={(stats.engagement / maxEngagement) * 100}
                        className="h-2"
                      />
                    </div>

                    {/* Reach */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          Reach
                        </span>
                        <span className="font-medium">{formatNumber(stats.reach)}</span>
                      </div>
                      <Progress
                        value={(stats.reach / maxReach) * 100}
                        className="h-2"
                      />
                    </div>
                  </div>

                  {/* Engagement Rate Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Engagement Rate:</span>
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded-full",
                      stats.engagementRate >= 5 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                      stats.engagementRate >= 2 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" :
                      "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    )}>
                      {stats.engagementRate.toFixed(2)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformComparison;
